import HowToRentStep from '@/components/onboarding/HowToRentStep.vue'
import useModals from '@/composables/modals.js'
import { useToastNotifications } from '@/composables/toastNotifications.js'
import { useToastNotificationsStore } from '@/stores/toastNotifications.js'
import useAnalytics from '@shared/composables/analytics.js'
import { useClientStore } from '@shared/stores/client.js'
import { whenever } from '@vueuse/core'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

export const useOnboardingStore = defineStore('onboarding', () => {
  const { showOnboardingToast } = useToastNotifications()
  const { dismissToastNotification } = useToastNotificationsStore()
  const { openOnboardingModal, closeOnboardingModal } = useModals()
  const { trackOnboardingInteraction } = useAnalytics()
  const route = useRoute()

  const clientStore = useClientStore()
  const { onboardingStep } = storeToRefs(clientStore)
  const { updateClient } = clientStore

  const steps = computed(() => [
    {
      title: 'Let the fun begin!',
      text: 'You have 3 days to pick your first case.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/funBegin.png',
      buttonText: 'Let\'s Go!',
    },
    {
      title: 'Compliments incoming!',
      component: HowToRentStep,
    },
    {
      title: 'Closet magic, explained',
      text: 'Your closet is curated by category to match your style and fit. 💜 or 👎 to improve your recs.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/closetMagic.png',
    },
    {
      title: 'On a mission?',
      text: 'Search or browse by category, brand, or occasion to tap into the endless closet.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/aMission.png',
    },
    {
      title: 'Style besties, reporting for duty',
      text: 'Check the reviews for real life fit notes and how-they-wore-it pics.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/styleBesties.png',
    },
    {
      title: 'Play the field',
      text: 'Rate new styles as they\'re added to the Big Closet. We\'ll use your votes to further tailor your recs.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/playTheField.png',
      buttonText: 'Continue',
      actionButton: {
        text: 'Play The Field',
        variant: 'stylist',
        to: '/style-games',
        onClick: () => {
          closeOnboardingModal()
          trackOnboardingInteraction('click', { button: 'style-games' })
        }
      }
    },
    {
      title: 'Your dream closet awaits',
      text: 'Need help picking that first case? Our stylists are just a call away.',
      imageUrl: 'https://d2ezpplblfgpjx.cloudfront.net/onboarding/dreamCloset.png',
      buttonText: 'View My Closet',
      actionButton: {
        text: 'Book a Call',
        variant: 'stylist',
        to: 'https://armoire.as.me/style-my-case',
        onClick: () => {
          trackOnboardingInteraction('click', { button: 'book-a-call' })
        }
      }
    }
  ].map((step, index, steps) => ({
    ...step,
    isFirstStep: index === 0,
    showBackButton: index > 0 && index < steps.length
  })))

  const currentStepIndex = ref(Math.min(Math.max(0, onboardingStep.value), steps.value.length))
  const onboardingToastId = ref(null)
  const triggered = ref(false)

  const progress = computed(() => ({
    step: Math.min(currentStepIndex.value, steps.value.length),
    totalSteps: steps.value.length,
  }))
  const completed = computed(() => Math.max(currentStepIndex.value, onboardingStep.value) >= steps.value.length)
  const toastMessage = computed(() => {
    const text = completed.value
      ? 'Getting Started'
      : `Getting Started ${progress.value.step}/${progress.value.totalSteps}`
    return {
      text,
      bold: true
    }
  })

  function onMyClosetRoute(callback) {
    whenever(() => route.name === 'closet-sections', callback, {
      immediate: true,
      once: true,
    })
  }

  function openModal() {
    if (completed.value) {
      currentStepIndex.value = 1
    }
    openOnboardingModal()
  }

  function triggerOnboardingFlow() {
    if (triggered.value) {
      return
    }
    triggered.value = true
    onMyClosetRoute(() => {
      if (onboardingToastId.value === null) {
        onboardingToastId.value = showOnboardingToast(toastMessage, openModal)
      }
      if (onboardingStep.value === null) {
        openModal()
      }
    })
  }

  function dismissOnboardingFlow() {
    closeOnboardingModal()
    if (onboardingToastId.value !== null) {
      dismissToastNotification(onboardingToastId.value)
      onboardingToastId.value = null
    }
  }

  async function onboardingStepViewed(index) {
    currentStepIndex.value = Math.min(Math.max(0, index), steps.value.length)
    if (currentStepIndex.value > (onboardingStep.value ?? -1)) {
      await updateClient({ onboardingStep: currentStepIndex.value })
    }
  }

  return {
    currentStepIndex,
    onboardingToastId,
    triggered,
    steps,
    progress,
    completed,
    toastMessage,
    triggerOnboardingFlow,
    dismissOnboardingFlow,
    onboardingStepViewed,
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useOnboardingStore, import.meta.hot))
